import {
  AnthropicProvider,
  DeepInfraProvider,
  GroqProvider,
  OllamaProvider,
  OpenAIProvider,
  WebLLMProvider,
  BudgetManager,
  DailyBudgetExceededError,
  type ChatMessage,
  type BudgetManagerConfig,
  type LLMProvider,
  type OperationType,
  type TokenUsage,
  getModelPricing,
} from '@ownyou/llm-client';
import { getLLMProxyBaseUrl } from '../openai-proxy';
import {
  IAB_CLASSIFICATION_ROUTE_IDS,
  IAB_INPUT_PREPROCESSING_ROUTE_ID,
  IKIGAI_MODEL_ROUTE_IDS,
  MISSION_WORKER_MODEL_ROUTE_IDS,
  MODEL_ROUTE_IDS,
  getModelRouteDefinition,
  type ModelRouteId,
} from './routes';
import {
  normalizeLlmProviderId,
  type LlmProviderId,
} from './preferences-v2';
import type {
  LlmRouteExecutorBoundary,
  RouteValidationPolicy,
} from './types';
import { emitAuditProducerEvent } from '../audit';

export type LlmRouteSelectionSource = 'user' | 'default' | 'experiment';

export type LlmRouteExecutionErrorCode =
  | 'missing_selection'
  | 'invalid_selection'
  | 'unavailable_selection'
  | 'unsupported_provider'
  | 'missing_auth'
  | 'provider_error'
  | 'validation_failed'
  | 'missing_route_plan'
  | 'missing_route_selection'
  | 'budget_deferred'
  | 'budget_blocked'
  | 'aborted';

export type LlmRouteExecutableProviderId = Exclude<LlmProviderId, 'google'>;
export type Gate03LlmProviderId = LlmRouteExecutableProviderId;

export interface LlmRouteModelSelection {
  provider?: string | null;
  model?: string | null;
}

export interface LlmRouteModelCatalogEntry {
  provider: string;
  model: string;
}

export interface LlmRouteProviderFactoryInput {
  provider: LlmRouteExecutableProviderId;
  model: string;
  authToken?: string | null;
}

export type LlmRouteProviderFactory = (input: LlmRouteProviderFactoryInput) => LLMProvider;

export interface ExecuteLlmTextRouteRequest {
  routeId: string;
  selection?: LlmRouteModelSelection | null;
  selectionSource: LlmRouteSelectionSource;
  selectionReason?: string | null;
  messages: ChatMessage[];
  userId?: string | null;
  authToken?: string | null;
  temperature?: number;
  maxTokens?: number;
  operation?: OperationType;
  budgetConfig?: BudgetManagerConfig;
  modelCatalog?: readonly LlmRouteModelCatalogEntry[];
  emitTelemetry?: (event: LlmRouteTelemetryEvent) => void;
  providerFactory?: LlmRouteProviderFactory;
}

export interface LlmRouteTextResult {
  routeId: ModelRouteId;
  provider: LlmRouteExecutableProviderId;
  model: string;
  text: string;
  usage?: TokenUsage;
  validation: LlmRouteValidationResult;
}

export type LlmRouteValidationResult =
  | {
      status: 'not_run';
      policyKind?: RouteValidationPolicy['kind'];
    }
  | {
      status: 'passed' | 'failed';
      policyKind: 'nonEmptyText';
      minChars: number;
      outputChars: number;
    }
  | {
      status: 'failed';
      policyKind: Exclude<RouteValidationPolicy['kind'], 'nonEmptyText' | 'jsonSchema'>;
      reason: string;
    };

export interface LlmRouteTelemetryEvent {
  type: 'llm_route_execution';
  routeId: string;
  provider: LlmRouteExecutableProviderId | null;
  model: string | null;
  selectionSource: LlmRouteSelectionSource | null;
  selectionReason: string | null;
  routePolicyVersion: string | null;
  promptVersion: string | null;
  outputSchemaVersion: string | null;
  validation: LlmRouteValidationResult;
  result: 'success' | 'error';
  errorCode?: LlmRouteExecutionErrorCode;
  latencyMs: number;
  usage?: TokenUsage;
  costKnown: boolean;
  costBucket?: 'known' | 'unknown_reserved' | 'not_applicable';
  budgetStatus?: 'ok' | 'warn' | 'downgrade_ignored' | 'deferred' | 'blocked' | 'not_checked';
  budgetPercent?: number;
  privacyClass: string | null;
  operation: OperationType | null;
}

interface ErrorContext {
  routeId: string;
  provider?: string | null;
  model?: string | null;
  details?: Record<string, unknown>;
}

export class LlmRouteExecutionError extends Error {
  readonly code: LlmRouteExecutionErrorCode;
  readonly routeId: string;
  readonly provider?: string | null;
  readonly model?: string | null;
  readonly details?: Record<string, unknown>;

  constructor(
    code: LlmRouteExecutionErrorCode,
    message: string,
    context: ErrorContext,
  ) {
    super(message);
    this.name = 'LlmRouteExecutionError';
    this.code = code;
    this.routeId = context.routeId;
    this.provider = context.provider;
    this.model = context.model;
    this.details = context.details;
  }
}

const LLM_ROUTE_ALLOWED_PROVIDERS = [
  'openai',
  'anthropic',
  'groq',
  'deepinfra',
  'xai',
  'ollama',
  'webllm',
] as const satisfies readonly LlmRouteExecutableProviderId[];

const LLM_ROUTE_ALLOWED_PROVIDER_SET: ReadonlySet<string> = new Set(LLM_ROUTE_ALLOWED_PROVIDERS);
export const LLM_ROUTE_CLOUD_PROVIDERS = [
  'openai',
  'anthropic',
  'groq',
  'deepinfra',
  'xai',
] as const satisfies readonly LlmRouteExecutableProviderId[];

const LLM_ROUTE_CLOUD_PROVIDER_SET: ReadonlySet<string> = new Set(LLM_ROUTE_CLOUD_PROVIDERS);

export function isLlmRouteExecutableProvider(provider: string | null | undefined): provider is LlmRouteExecutableProviderId {
  const canonical = normalizeLlmProviderId(provider);
  return Boolean(canonical && LLM_ROUTE_ALLOWED_PROVIDER_SET.has(canonical));
}

const IAB_EXECUTOR_ROUTE_IDS = [
  IAB_INPUT_PREPROCESSING_ROUTE_ID,
  ...IAB_CLASSIFICATION_ROUTE_IDS,
] as const satisfies readonly ModelRouteId[];

const LLM_TEXT_EXECUTOR_ROUTE_IDS = [
  ...IAB_EXECUTOR_ROUTE_IDS,
  ...IKIGAI_MODEL_ROUTE_IDS,
  ...MISSION_WORKER_MODEL_ROUTE_IDS,
] as const satisfies readonly ModelRouteId[];

const LLM_TEXT_EXECUTOR_ROUTE_ID_SET: ReadonlySet<string> = new Set(LLM_TEXT_EXECUTOR_ROUTE_IDS);

export function isLlmRouteCloudProvider(provider: string | null | undefined): boolean {
  const canonical = normalizeLlmProviderId(provider);
  return Boolean(canonical && LLM_ROUTE_CLOUD_PROVIDER_SET.has(canonical));
}

export function isGate03RouteCloudProvider(provider: string | null | undefined): boolean {
  return isLlmRouteCloudProvider(provider);
}

export function isLlmRouteExecutionError(error: unknown): error is LlmRouteExecutionError {
  return error instanceof LlmRouteExecutionError;
}

export function createLlmRouteProvider({
  provider,
  model,
  authToken,
}: LlmRouteProviderFactoryInput): LLMProvider {
  const ollamaBaseUrl = import.meta.env.VITE_OLLAMA_BASE_URL || undefined;

  switch (provider) {
    case 'openai':
      return new OpenAIProvider({
        apiKey: authToken ?? '',
        baseUrl: getLLMProxyBaseUrl('openai'),
        model,
      });
    case 'anthropic':
      return new AnthropicProvider({
        apiKey: authToken ?? '',
        baseUrl: getLLMProxyBaseUrl('anthropic'),
        model,
      });
    case 'groq':
      return new GroqProvider({
        apiKey: authToken ?? '',
        baseUrl: getLLMProxyBaseUrl('groq'),
        model,
      });
    case 'deepinfra':
      return new DeepInfraProvider({
        apiKey: authToken ?? '',
        baseUrl: getLLMProxyBaseUrl('deepinfra'),
        model,
      });
    case 'xai':
      return new OpenAIProvider({
        apiKey: authToken ?? '',
        baseUrl: getLLMProxyBaseUrl('xai'),
        model,
      });
    case 'ollama':
      return new OllamaProvider({ model, baseUrl: ollamaBaseUrl });
    case 'webllm':
      return new WebLLMProvider({ model });
  }
}

export const createGate03LlmProvider = createLlmRouteProvider;

function emitTelemetry(
  request: ExecuteLlmTextRouteRequest,
  event: LlmRouteTelemetryEvent,
): void {
  try {
    request.emitTelemetry?.(event);
  } catch (error) {
    console.warn('[LlmRouteExecutor] Telemetry sink failed:', error);
  }
}

function createTelemetryEvent(
  request: ExecuteLlmTextRouteRequest,
  params: {
    provider: Gate03LlmProviderId | null;
    model: string | null;
    validation: LlmRouteValidationResult;
    result: 'success' | 'error';
    startTime: number;
    errorCode?: LlmRouteExecutionErrorCode;
    usage?: TokenUsage;
    budgetStatus?: LlmRouteTelemetryEvent['budgetStatus'];
    budgetPercent?: number;
    costBucket?: LlmRouteTelemetryEvent['costBucket'];
    operation?: OperationType | null;
  },
): LlmRouteTelemetryEvent {
  const route = getModelRouteDefinition(request.routeId);
  return {
    type: 'llm_route_execution',
    routeId: request.routeId,
    provider: params.provider,
    model: params.model,
    selectionSource: request.selectionSource ?? null,
    selectionReason: request.selectionReason ?? null,
    routePolicyVersion: route?.routePolicyVersion ?? null,
    promptVersion: route?.promptVersion ?? null,
    outputSchemaVersion: route?.outputSchemaVersion ?? null,
    validation: params.validation,
    result: params.result,
    errorCode: params.errorCode,
    latencyMs: Date.now() - params.startTime,
    usage: params.usage,
    costKnown: params.costBucket === 'unknown_reserved'
      ? false
      : typeof params.usage?.costUsd === 'number',
    costBucket: params.costBucket ?? (typeof params.usage?.costUsd === 'number' ? 'known' : 'not_applicable'),
    budgetStatus: params.budgetStatus ?? 'not_checked',
    budgetPercent: params.budgetPercent,
    privacyClass: route?.modelFitPolicy.privacyPosture ?? null,
    operation: params.operation ?? null,
  };
}

function fail(
  request: ExecuteLlmTextRouteRequest,
  startTime: number,
  code: LlmRouteExecutionErrorCode,
  message: string,
  params: {
    provider?: Gate03LlmProviderId | null;
    model?: string | null;
    validation?: LlmRouteValidationResult;
    details?: Record<string, unknown>;
    budgetStatus?: LlmRouteTelemetryEvent['budgetStatus'];
    budgetPercent?: number;
    costBucket?: LlmRouteTelemetryEvent['costBucket'];
    operation?: OperationType | null;
  } = {},
): never {
  emitTelemetry(
    request,
    createTelemetryEvent(request, {
      provider: params.provider ?? null,
      model: params.model ?? null,
      validation: params.validation ?? {
        status: 'not_run',
        policyKind: getModelRouteDefinition(request.routeId)?.validationPolicy.kind,
      },
      result: 'error',
      startTime,
      errorCode: code,
      budgetStatus: params.budgetStatus,
      budgetPercent: params.budgetPercent,
      costBucket: params.costBucket,
      operation: params.operation,
    }),
  );

  throw new LlmRouteExecutionError(code, message, {
    routeId: request.routeId,
    provider: params.provider,
    model: params.model,
    details: params.details,
  });
}

function assertSupportedRoute(
  request: ExecuteLlmTextRouteRequest,
  startTime: number,
): ModelRouteId {
  const route = getModelRouteDefinition(request.routeId);
  if (!route || !LLM_TEXT_EXECUTOR_ROUTE_ID_SET.has(route.id)) {
    fail(
      request,
      startTime,
      'invalid_selection',
      'Route executor only supports declared IAB and Gate 05 mission worker routes',
      { details: { requestedRouteId: request.routeId } },
    );
  }

  return route.id;
}

function routeOperation(routeId: ModelRouteId, requestedOperation?: OperationType): OperationType {
  if (requestedOperation) return requestedOperation;

  switch (routeId) {
    case MODEL_ROUTE_IDS.ikigai.sourceSummary:
    case MODEL_ROUTE_IDS.ikigai.entitySynthesis:
    case MODEL_ROUTE_IDS.ikigai.selfAudit:
    case MODEL_ROUTE_IDS.ikigai.entityComposition:
      return 'ikigai_inference';
    case MODEL_ROUTE_IDS.missions.refinement:
      return 'mission_refinement';
    case MODEL_ROUTE_IDS.missions.diagnosticInsights:
      return 'reflection_node';
    case MODEL_ROUTE_IDS.ikigai.profileSummary:
      return 'character_summary';
    case MODEL_ROUTE_IDS.missions.intentExtraction:
    case MODEL_ROUTE_IDS.missions.searchKeywords:
    case MODEL_ROUTE_IDS.missions.agentExecution:
    case MODEL_ROUTE_IDS.missions.predictionText:
      return 'mission_agent';
    default:
      return 'iab_classification';
  }
}

function resolveSelection(
  request: ExecuteLlmTextRouteRequest,
  startTime: number,
): { provider: LlmRouteExecutableProviderId; model: string } {
  const providerInput = request.selection?.provider?.trim();
  const model = request.selection?.model?.trim();
  if (!providerInput || !model) {
    fail(request, startTime, 'missing_selection', 'Route execution requires provider and model selection');
  }

  const provider = normalizeLlmProviderId(providerInput);
  if (!provider) {
    fail(request, startTime, 'invalid_selection', `Unknown LLM provider: ${providerInput}`, {
      model,
      details: { selectedProvider: providerInput },
    });
  }

  if (provider === 'google') {
    fail(
      request,
      startTime,
      'unsupported_provider',
      'Google/Gemini execution is not supported until /api/llm/v1/google is available',
      { model, details: { selectedProvider: providerInput } },
    );
  }

  if (!LLM_ROUTE_ALLOWED_PROVIDER_SET.has(provider)) {
    fail(request, startTime, 'unsupported_provider', `Provider is not supported by the route executor: ${provider}`, {
      model,
      details: { selectedProvider: providerInput },
    });
  }

  return { provider: provider as LlmRouteExecutableProviderId, model };
}

function assertCatalogAvailability(
  request: ExecuteLlmTextRouteRequest,
  startTime: number,
  provider: LlmRouteExecutableProviderId,
  model: string,
): void {
  if (!request.modelCatalog) return;

  const available = request.modelCatalog.some((entry) => (
    normalizeLlmProviderId(entry.provider) === provider && entry.model === model
  ));
  if (available) return;

  fail(
    request,
    startTime,
    'unavailable_selection',
    `Selected model is not available in the current catalog: ${provider}:${model}`,
    { provider, model },
  );
}

function assertRequestShape(
  request: ExecuteLlmTextRouteRequest,
  startTime: number,
  provider: LlmRouteExecutableProviderId,
  model: string,
): void {
  if (!['user', 'default', 'experiment'].includes(request.selectionSource)) {
    fail(request, startTime, 'invalid_selection', 'Route execution requires a valid selection source', {
      provider,
      model,
      details: { selectionSource: request.selectionSource },
    });
  }

  if (!Array.isArray(request.messages) || request.messages.length === 0) {
    fail(request, startTime, 'validation_failed', 'Route execution requires at least one message', {
      provider,
      model,
    });
  }
}

function assertAuth(
  request: ExecuteLlmTextRouteRequest,
  startTime: number,
  provider: LlmRouteExecutableProviderId,
  model: string,
): void {
  if (!LLM_ROUTE_CLOUD_PROVIDER_SET.has(provider)) return;
  if (request.authToken?.trim()) return;

  fail(request, startTime, 'missing_auth', `Missing OwnYou proxy auth token for ${provider}`, {
    provider,
    model,
  });
}

function validateTextOutput(
  policy: RouteValidationPolicy,
  text: string,
): LlmRouteValidationResult {
  if (policy.kind === 'jsonSchema') {
    // Gate 04 executes JSON-schema routes as text; package-level parsers validate
    // structured IAB output. Executor-level JSON Schema validation is deferred.
    return {
      status: 'not_run',
      policyKind: policy.kind,
    };
  }

  if (policy.kind !== 'nonEmptyText') {
    return {
      status: 'failed',
      policyKind: policy.kind,
      reason: 'unsupported-validation-policy',
    };
  }

  const outputChars = text.trim().length;
  return {
    status: outputChars >= policy.minChars ? 'passed' : 'failed',
    policyKind: policy.kind,
    minChars: policy.minChars,
    outputChars,
  };
}

function estimateInputTokens(messages: readonly ChatMessage[]): number {
  const chars = messages.reduce((total, message) => total + message.content.length, 0);
  return Math.max(1, Math.ceil(chars / 4));
}

function estimateRequestCostUsd(
  provider: LlmRouteExecutableProviderId,
  model: string,
  messages: readonly ChatMessage[],
  maxTokens: number | undefined,
  budgetManager: BudgetManager,
): { amount: number; costKnown: boolean; costBucket: LlmRouteTelemetryEvent['costBucket'] } {
  if (!LLM_ROUTE_CLOUD_PROVIDER_SET.has(provider)) {
    return { amount: 0, costKnown: true, costBucket: 'not_applicable' };
  }

  const pricing = getModelPricing(model);
  if (!pricing) {
    return {
      amount: budgetManager.getUnknownRequestReserveUsd(),
      costKnown: false,
      costBucket: 'unknown_reserved',
    };
  }

  const inputTokens = estimateInputTokens(messages);
  const outputTokens = Math.max(1, maxTokens ?? 1000);
  return {
    amount: (inputTokens / 1000) * pricing.inputPer1k + (outputTokens / 1000) * pricing.outputPer1k,
    costKnown: true,
    costBucket: 'known',
  };
}

async function enforceBudgetBeforePrompt(
  request: ExecuteLlmTextRouteRequest,
  startTime: number,
  provider: LlmRouteExecutableProviderId,
  model: string,
  operation: OperationType,
): Promise<{
  manager: BudgetManager | null;
  budgetStatus: NonNullable<LlmRouteTelemetryEvent['budgetStatus']>;
  budgetPercent?: number;
  estimatedCostUsd: number;
  estimatedCostKnown: boolean;
  costBucket: LlmRouteTelemetryEvent['costBucket'];
}> {
  if (!request.budgetConfig || !request.userId || !LLM_ROUTE_CLOUD_PROVIDER_SET.has(provider)) {
    return {
      manager: null,
      budgetStatus: 'not_checked',
      estimatedCostUsd: 0,
      estimatedCostKnown: true,
      costBucket: LLM_ROUTE_CLOUD_PROVIDER_SET.has(provider) ? 'known' : 'not_applicable',
    };
  }

  const manager = new BudgetManager(request.budgetConfig);
  const decision = await manager.getThrottleDecision(request.userId);
  if (decision.action === 'block') {
    fail(request, startTime, 'budget_blocked', 'Route execution blocked by LLM budget policy', {
      provider,
      model,
      budgetStatus: 'blocked',
      budgetPercent: decision.budgetPercent,
      operation,
    });
  }
  if (decision.action === 'defer') {
    fail(request, startTime, 'budget_deferred', 'Route execution deferred by LLM budget policy', {
      provider,
      model,
      budgetStatus: 'deferred',
      budgetPercent: decision.budgetPercent,
      operation,
    });
  }

  const estimate = estimateRequestCostUsd(provider, model, request.messages, request.maxTokens, manager);
  try {
    await manager.assertDailyBudgetAvailable(request.userId, estimate.amount);
  } catch (error) {
    if (error instanceof DailyBudgetExceededError) {
      fail(request, startTime, 'budget_blocked', error.message, {
        provider,
        model,
        budgetStatus: 'blocked',
        budgetPercent: decision.budgetPercent,
        costBucket: estimate.costBucket,
        operation,
      });
    }
    throw error;
  }

  return {
    manager,
    budgetStatus: decision.action === 'downgrade'
      ? 'downgrade_ignored'
      : decision.action,
    budgetPercent: decision.budgetPercent,
    estimatedCostUsd: estimate.amount,
    estimatedCostKnown: estimate.costKnown,
    costBucket: estimate.costBucket,
  };
}

export async function executeLlmTextRoute(
  request: ExecuteLlmTextRouteRequest,
): Promise<LlmRouteTextResult> {
  const startTime = Date.now();
  const routeId = assertSupportedRoute(request, startTime);
  const route = getModelRouteDefinition(routeId);
  const { provider, model } = resolveSelection(request, startTime);
  const operation = routeOperation(routeId, request.operation);

  assertCatalogAvailability(request, startTime, provider, model);
  assertRequestShape(request, startTime, provider, model);
  assertAuth(request, startTime, provider, model);
  const budget = await enforceBudgetBeforePrompt(request, startTime, provider, model, operation);

  const providerFactory = request.providerFactory ?? createLlmRouteProvider;
  let llmProvider: LLMProvider;
  try {
    llmProvider = providerFactory({
      provider,
      model,
      authToken: request.authToken,
    });
  } catch (error) {
    fail(request, startTime, 'provider_error', error instanceof Error ? error.message : String(error), {
      provider,
      model,
      budgetStatus: budget.budgetStatus,
      budgetPercent: budget.budgetPercent,
      costBucket: budget.costBucket,
      operation,
    });
  }

  let response;
  try {
    response = await llmProvider.complete({
      messages: request.messages,
      model,
      temperature: request.temperature,
      maxTokens: request.maxTokens,
      operation,
    });
  } catch (error) {
    fail(request, startTime, 'provider_error', error instanceof Error ? error.message : String(error), {
      provider,
      model,
      budgetStatus: budget.budgetStatus,
      budgetPercent: budget.budgetPercent,
      costBucket: budget.costBucket,
      operation,
    });
  }

  if (response.error) {
    fail(request, startTime, 'provider_error', response.error, {
      provider,
      model,
      details: { finishReason: response.finishReason },
      budgetStatus: budget.budgetStatus,
      budgetPercent: budget.budgetPercent,
      costBucket: budget.costBucket,
      operation,
    });
  }

  const validation = validateTextOutput(route.validationPolicy, response.content);
  if (validation.status === 'failed') {
    fail(request, startTime, 'validation_failed', 'Provider response failed route text validation', {
      provider,
      model,
      validation,
      budgetStatus: budget.budgetStatus,
      budgetPercent: budget.budgetPercent,
      operation,
    });
  }

  if (budget.manager && request.userId) {
    await budget.manager.trackUsage(request.userId, {
      model: response.model,
      inputTokens: response.usage.inputTokens,
      outputTokens: response.usage.outputTokens,
      costUsd: budget.estimatedCostKnown ? response.usage.costUsd : budget.estimatedCostUsd,
      operation,
      timestamp: Date.now(),
    });
  }

  emitTelemetry(
    request,
    createTelemetryEvent(request, {
      provider,
      model,
      validation,
      result: 'success',
      startTime,
      usage: response.usage,
      budgetStatus: budget.budgetStatus,
      budgetPercent: budget.budgetPercent,
      costBucket: budget.costBucket,
      operation,
    }),
  );
  await emitAuditProducerEvent({
    actor: { kind: 'system' },
    action: 'llm.route_executed',
    target: {
      kind: 'llm_route',
      stableRef: `route:${routeId}`,
      label: routeId,
    },
    result: 'success',
    routeId,
  });

  return {
    routeId,
    provider,
    model,
    text: response.content,
    usage: response.usage,
    validation,
  };
}

export class LlmRouteExecutor implements LlmRouteExecutorBoundary {
  async execute(routeId: string, request: Omit<ExecuteLlmTextRouteRequest, 'routeId'>): Promise<LlmRouteTextResult> {
    return executeLlmTextRoute({ ...request, routeId });
  }
}
